import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useActors, useAgentImportPreview, useApplyAgentImport, useProjects } from "../api.ts";
import { canApplyImport, defaultImportApprovals, INSTRUCTION_CHAR_CAP as CAP, selectedInstructionChars, type ImportDiff, type ImportPreviewDTO, type PendingItem } from "../agent-import.ts";
import { Action, Block, Card, Page, PageHeader, Pill, Switch } from "../calm.tsx";
import { teamPeople } from "../roster.ts";
import { cx, ErrorNote, inputCls } from "../ui.tsx";

function fallbackInstructionFiles(preview: ImportPreviewDTO) {
  const chunks = preview.plan.agentConfig.instructions.split(/(?=^##\s)/m).filter(Boolean);
  return chunks.map((content, index) => {
    const heading = content.match(/^##\s+(.+)$/m)?.[1]?.trim() ?? `Instructions ${index + 1}`;
    return { path: heading, content, chars: content.length, selected: true };
  });
}

function isBroadBash(rule: string) {
  return /^Bash(?:\(|$)/.test(rule) && (!rule.includes("(") || /^Bash\(\s*\*/.test(rule));
}

function ToolList({ title, items, approval = false }: { title: string; items: string[]; approval?: boolean }) {
  return (
    <div className="min-w-0 rounded-[16px] bg-ink/[0.035] p-4">
      <h3 className="mb-3 text-[12.5px] font-medium text-ink-2">{title}</h3>
      {items.length ? (
        <ul className="space-y-2">
          {items.map((item) => {
            const broad = isBroadBash(item);
            return (
              <li key={item} className={cx("break-all font-mono text-[12px]", broad && approval ? "text-high" : "text-ink-2")}>
                {item} {broad && approval && <span className="font-sans text-[11px]">· broad rule</span>}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-[12.5px] italic text-ink-3">None</p>
      )}
    </div>
  );
}

function ExactRiskItem({ item, enabled, onChange }: { item: PendingItem; enabled: boolean; onChange: (next: boolean) => void }) {
  return (
    <li className="rounded-[16px] border border-rule bg-paper/40 p-4" data-testid="untrusted-item">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Pill tone="signal">needs approval</Pill>
            <span className="text-[13px] font-medium">{item.kind.replace("_", " ")}</span>
            <span className="text-[12px] text-ink-3">from {item.sources.join(", ")}</span>
          </div>
          <code className="mt-3 block whitespace-pre-wrap break-all rounded-md bg-ink px-3 py-2 font-mono text-[12px] leading-relaxed text-paper" data-testid="untrusted-exact-value">
            {item.detail}
          </code>
        </div>
        <Switch checked={enabled} onChange={onChange} label={enabled ? "Included" : "Off"} testId={`approve-${item.id}`} />
      </div>
    </li>
  );
}

export function DiffView({ diff }: { diff: ImportDiff | null }) {
  if (!diff) return <p className="py-8 text-center text-[13px] italic text-ink-3">No earlier import to compare.</p>;
  const groups = [
    ["Tools", diff.tools],
    ["MCP servers", diff.mcpServers],
    ["Hooks", diff.hooks],
    ["Subagents", { added: diff.subagents.added, removed: diff.subagents.removed }],
  ] as const;
  return (
    <div className="space-y-6" data-testid="import-diff">
      <div>
        <h3 className="mb-2 text-[13px] font-medium text-ink-2">Instructions · line diff</h3>
        <pre className="overflow-x-auto rounded-[14px] bg-ink p-4 font-mono text-[12px] leading-relaxed text-paper">
          {(diff.instructionLines ?? [{ kind: "same" as const, text: diff.instructionsChanged ? "Instructions changed; line detail unavailable from this preview." : "No instruction changes." }]).map((line, index) => (
            <span key={index} className={cx("block", line.kind === "added" && "text-low", line.kind === "removed" && "text-high")}>
              {line.text}
            </span>
          ))}
        </pre>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {groups.map(([label, values]) => (
          <div key={label} className="rounded-[14px] bg-ink/[0.035] p-3">
            <h4 className="text-[12.5px] font-medium">{label}</h4>
            <ul className="mt-2 space-y-1 font-mono text-[11.5px]">
              {values.added.map((x) => <li key={`+${x}`} className="text-low">+ {x}</li>)}
              {values.removed.map((x) => <li key={`-${x}`} className="text-high">− {x}</li>)}
              {!values.added.length && !values.removed.length && <li className="font-sans italic text-ink-3">No change</li>}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AgentImportPage() {
  const { previewId = "" } = useParams();
  const navigate = useNavigate();
  const previewQuery = useAgentImportPreview(previewId);
  const actors = useActors().data ?? [];
  const humans = teamPeople(actors);
  const projects = useProjects().data ?? [];
  const apply = useApplyAgentImport();
  const preview = previewQuery.data;
  const [tab, setTab] = useState<"preview" | "diff">("preview");
  const [selectedMain, setSelectedMain] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [ownerId, setOwnerId] = useState<string | null>(null);
  const [areaDraft, setAreaDraft] = useState("");
  const [allowedAreas, setAllowedAreas] = useState<string[]>([]);
  const [unfenced, setUnfenced] = useState(false);
  const [approvals, setApprovals] = useState<Record<string, boolean>>({});
  const [selectedFiles, setSelectedFiles] = useState<Record<string, boolean>>({});

  const instructionFiles = useMemo(() => (preview ? preview.instructionFiles ?? fallbackInstructionFiles(preview) : []), [preview]);
  if (previewQuery.isPending) return <Page><p className="font-mono text-[12px] text-ink-3">Reading import preview…</p></Page>;
  if (!preview) return <Page><ErrorNote error={previewQuery.error ?? new Error("Import preview not found or expired.")} /></Page>;

  const instructionChars = selectedInstructionChars(instructionFiles, selectedFiles);
  const effectiveName = name ?? preview.plan.actor.name;
  const effectiveOwner = ownerId ?? humans[0]?.id ?? "";
  const errors = [...preview.plan.errors, ...(instructionChars > CAP ? [`Instructions are ${instructionChars.toLocaleString()} characters; deselect files to reach ${CAP.toLocaleString()}.`] : [])];
  const effectiveApprovals = { ...defaultImportApprovals(preview.plan.pending), ...approvals };
  const canApply = canApplyImport({ name: effectiveName, ownerId: effectiveOwner, errors, instructionChars, allowedAreas, unfenced });
  const approvalItems = preview.plan.pending.filter((item) => item.kind === "allow_rule");
  // Whatever the server labels "safe", still drop anything that also shows up as needs-approval,
  // so a rule can't double up in both the safe and the approval column.
  const safe = (preview.safeAddedTools ?? preview.plan.agentConfig.allowedTools).filter((tool) => !approvalItems.some((item) => item.detail === tool));
  const excludedFiles = instructionFiles.filter((file) => !(selectedFiles[file.path] ?? file.selected ?? true)).map((file) => file.path);
  const addArea = () => {
    const value = areaDraft.trim();
    if (value && !allowedAreas.includes(value)) setAllowedAreas((current) => [...current, value]);
    setAreaDraft("");
  };
  const submit = () => apply.mutate(
    { id: preview.id, name: effectiveName.trim(), ownerId: effectiveOwner, allowedAreas, skills: [], maxParallel: 2, excludedFiles, approve: Object.entries(effectiveApprovals).filter(([, on]) => on).map(([id]) => id).concat(unfenced ? ["unfenced"] : []) },
    { onSuccess: (actor) => navigate(`/agents/${actor.id}/config`) },
  );

  return (
    <Page wide>
      <PageHeader
        crumbs={[{ label: "Team", to: "/team" }, { label: "Import agent" }]}
        title="Review imported agent"
        meta={<span>Nothing untrusted runs until a human explicitly includes it.</span>}
        status={<Pill tone={errors.length ? "danger" : "neutral"}>{errors.length ? `${errors.length} blocking error${errors.length === 1 ? "" : "s"}` : "preview"}</Pill>}
      />

      <div className="mt-6 flex gap-1 border-b border-rule" role="tablist">
        <button type="button" role="tab" aria-selected={tab === "preview"} onClick={() => setTab("preview")} className={cx("border-b-2 px-3 py-2 text-[13px]", tab === "preview" ? "border-ink text-ink" : "border-transparent text-ink-3")} data-testid="import-preview-tab">
          Preview
        </button>
        <button type="button" role="tab" aria-selected={tab === "diff"} onClick={() => setTab("diff")} className={cx("border-b-2 px-3 py-2 text-[13px]", tab === "diff" ? "border-ink text-ink" : "border-transparent text-ink-3")} data-testid="import-diff-tab">
          Diff{preview.diff ? "" : " · none yet"}
        </button>
      </div>

      {tab === "diff" ? (
        <div className="mt-8">
          <DiffView diff={preview.diff} />
        </div>
      ) : (
      <div className="mt-8 space-y-8" data-testid="agent-import-preview">
        {errors.length > 0 && (
          <Card className="border border-high/40 !bg-high/[0.06]" data-testid="import-errors">
            <h2 className="text-[14px] font-medium text-high">Fix before applying</h2>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-[13px] text-high">{errors.map((error) => <li key={error}>{error}</li>)}</ul>
          </Card>
        )}

        <Block title="Identity" aside="The actor Roomate will create">
          <Card>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
              <label className="text-[12.5px] text-ink-2">Name<input className={cx(inputCls, "mt-1.5")} value={effectiveName} onChange={(event) => setName(event.target.value)} data-testid="import-name" /></label>
              {(preview.mainAgents?.length ?? 0) > 1 && <label className="text-[12.5px] text-ink-2">Main agent<select className={cx(inputCls, "mt-1.5")} value={selectedMain ?? preview.selectedMainAgent ?? preview.mainAgents![0]!.path} onChange={(event) => setSelectedMain(event.target.value)} data-testid="main-agent-picker">{preview.mainAgents!.map((agent) => <option key={agent.path} value={agent.path}>{agent.name} · {agent.path}</option>)}</select></label>}
              <label className="text-[12.5px] text-ink-2">Owner seat<select className={cx(inputCls, "mt-1.5")} value={effectiveOwner} onChange={(event) => setOwnerId(event.target.value)}>{humans.map((human) => <option key={human.id} value={human.id}>{human.name}</option>)}</select></label>
              <div><p className="text-[12.5px] text-ink-2">Model</p><p className="mt-2 font-mono text-[13px]">{preview.plan.actor.model ?? "seat default"}</p></div>
              <div className="md:col-span-2 lg:col-span-1"><p className="text-[12.5px] text-ink-2">Description</p><p className="mt-2 text-[13px] leading-relaxed">{preview.plan.actor.description}</p></div>
            </div>
          </Card>
        </Block>

        <Block title="Edit fence · required" aside="Imports never choose their own write access">
          <Card>
            <div className="flex flex-col gap-3 md:flex-row md:items-start">
              <div className="min-w-0 flex-1">
                <div className="flex gap-2">
                  <input className={inputCls} list="project-paths" value={areaDraft} onChange={(event) => setAreaDraft(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); addArea(); } }} placeholder="apps/web/**" aria-label="Allowed project path" data-testid="fence-path" />
                  <Action onClick={addArea}>Add area</Action>
                  <datalist id="project-paths">{projects.flatMap((project) => [project.localPath, project.name]).filter(Boolean).map((path) => <option key={path!} value={path!} />)}</datalist>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">{allowedAreas.map((area) => <button key={area} onClick={() => setAllowedAreas((current) => current.filter((item) => item !== area))} className="rounded-full bg-ink/[0.06] px-2.5 py-1 font-mono text-[11.5px]" title="Remove area">{area} ×</button>)}</div>
                {!allowedAreas.length && !unfenced && <p className="mt-2 text-[12px] text-high">Add at least one area. Apply stays disabled while the fence is empty.</p>}
              </div>
              <div className="rounded-[14px] bg-signal-soft p-3">
                <Switch checked={unfenced} onChange={setUnfenced} label="Allow edits anywhere" testId="allow-edits-anywhere" />
                <p className="mt-1.5 text-[11.5px] text-[color:var(--medium)]" data-testid="unfenced-approval-marker">{unfenced ? "Needs security approval · will raise a decision" : "Needs security approval if enabled"}</p>
              </div>
            </div>
          </Card>
        </Block>

        <Block title="Assembled instructions" aside={`${instructionChars.toLocaleString()} / ${CAP.toLocaleString()} characters`}>
          <Card>
            <div className="mb-4 h-1.5 overflow-hidden rounded-full bg-ink/[0.08]" aria-label={`${instructionChars} of ${CAP} instruction characters`}><div className={cx("h-full rounded-full", instructionChars > CAP ? "bg-high" : "bg-ink")} style={{ width: `${Math.min(100, instructionChars / CAP * 100)}%` }} /></div>
            <ul className="space-y-3" data-testid="instruction-files">
              {instructionFiles.map((file) => {
                const checked = selectedFiles[file.path] ?? file.selected ?? true;
                return <li key={file.path} className="rounded-[14px] bg-ink/[0.035] p-3"><label className="flex cursor-pointer items-start gap-3"><input type="checkbox" checked={checked} onChange={(event) => setSelectedFiles((current) => ({ ...current, [file.path]: event.target.checked }))} className="mt-1" /><span className="min-w-0 flex-1"><span className="flex flex-wrap justify-between gap-2"><code className="font-mono text-[12px]">{file.path}</code><span className="text-[11.5px] tabular-nums text-ink-3">{file.chars.toLocaleString()} chars</span></span><pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap font-mono text-[11.5px] leading-relaxed text-ink-2">{file.content}</pre></span></label></li>;
              })}
            </ul>
          </Card>
        </Block>

        <Block title="Tools" aside="Imported permissions can narrow access, never widen the edit fence">
          <Card>
            <div className="grid gap-3 lg:grid-cols-3">
              <ToolList title="Roomate base" items={preview.baseTools ?? ["Read", "Glob", "Grep", "WebFetch", "WebSearch"]} />
              <ToolList title="Added from import · safe" items={safe} />
              <ToolList title="Needs approval" items={approvalItems.map((item) => item.detail)} approval />
            </div>
            <div className="mt-4 rounded-[14px] border border-rule p-4"><h3 className="text-[12.5px] font-medium text-ink-2">Denied · applied automatically</h3><ul className="mt-2 grid gap-1 sm:grid-cols-2">{(preview.deniedTools ?? preview.plan.agentConfig.deniedTools).map((item) => <li key={item} className="break-all font-mono text-[12px] text-ink-2">{item}</li>)}</ul></div>
          </Card>
        </Block>

        <Block title="Subagents, skills and commands" aside="Renames and disabled capabilities remain visible">
          <Card pad={false}>
            <div className="overflow-x-auto"><table className="w-full text-left text-[12.5px]"><thead className="border-b border-rule text-ink-3"><tr><th className="px-5 py-3 font-medium">Kind</th><th className="px-5 py-3 font-medium">Imported</th><th className="px-5 py-3 font-medium">Result</th><th className="px-5 py-3 font-medium">Reason</th></tr></thead><tbody>
              {preview.plan.subagents.map((item) => <tr key={`sub-${item.sourceName}`} className="border-b border-rule/60"><td className="px-5 py-3">Subagent</td><td className="px-5 py-3 font-mono">{item.sourceName}</td><td className="px-5 py-3 font-mono">{item.name}</td><td className="px-5 py-3 text-ink-2">{item.reason ?? (item.renamed ? "Renamed to avoid a reserved name." : "Kept")}</td></tr>)}
              {preview.plan.skills.map((item) => <tr key={`skill-${item.path}`} className="border-b border-rule/60 last:border-0"><td className="px-5 py-3">{item.fromCommand ? "Command → skill" : "Skill"}</td><td className="px-5 py-3 font-mono">{item.name}</td><td className="px-5 py-3">{item.disabled ? <Pill tone="danger">disabled</Pill> : <Pill>included</Pill>}</td><td className="px-5 py-3 text-ink-2">{item.disabledReason ?? (item.strippedHooks ? "Hooks stripped" : item.strippedAllowedTools.length ? `Removed allowed-tools: ${item.strippedAllowedTools.join(", ")}` : "No unsafe capability found")}</td></tr>)}
            </tbody></table></div>
          </Card>
        </Block>

        <Block title="Untrusted · off by default" aside="Exact commands and URLs are never shortened">
          <ul className="space-y-3" data-testid="untrusted-list">{preview.plan.pending.map((item) => <ExactRiskItem key={item.id} item={item} enabled={effectiveApprovals[item.id] ?? false} onChange={(next) => setApprovals((current) => ({ ...current, [item.id]: next }))} />)}</ul>
        </Block>

        <Block title="Ignored" aside="Detected, recorded, not applied">
          <Card pad={false}><ul className="divide-y divide-rule">{preview.plan.ignored.map((item, index) => <li key={`${item.item}-${index}`} className="grid gap-1 px-5 py-3 sm:grid-cols-[minmax(0,1fr)_2fr]"><span><code className="break-all font-mono text-[12px]">{item.item}</code>{item.source && <span className="ml-2 text-[11.5px] text-ink-3">{item.source}</span>}</span><span className="text-[12.5px] text-ink-2">{item.reason}</span></li>)}</ul></Card>
        </Block>

        {preview.plan.warnings.length > 0 && <Block title="Warnings"><Card><ul className="list-disc space-y-1 pl-5 text-[13px] text-ink-2">{preview.plan.warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul></Card></Block>}
      </div>
      )}
      <ErrorNote error={apply.error} />
      <div className="sticky bottom-4 mt-8 flex flex-wrap items-center justify-between gap-3 rounded-[18px] border border-rule bg-card/95 p-4 shadow-xl backdrop-blur">
        <p className="text-[12.5px] text-ink-2">{canApply ? `${Object.values(effectiveApprovals).filter(Boolean).length + (unfenced ? 1 : 0)} approval request(s) will be raised.` : "Complete the required fence and resolve every error."}</p>
        <div className="flex gap-2"><Link to="/team" className="rounded-full bg-ink/[0.05] px-4 py-2 text-[13.5px]">Cancel</Link><Action primary disabled={!canApply || apply.isPending} onClick={submit} testId="apply-import">Apply import</Action></div>
      </div>
    </Page>
  );
}
